import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Banknote,
  BookOpenCheck,
  CalendarDays,
  CheckCircle2,
  Compass,
  GraduationCap,
  Loader2,
  Rocket,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  User,
  Zap,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import GovernmentSupportSection from "@/components/career-report/GovernmentSupportSection";
import PaywallTeaser from "@/components/PaywallTeaser";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  aiRiskLevel,
  deriveCandidateSummary,
  fmtMoney,
  hasReport,
  labelize,
  PATHWAY_META,
} from "@/lib/assessment";

const PATHWAY_ORDER = ["immediate_income", "transitional", "long_term_future_proof"];

const RISK_BADGE = {
  low: "bg-emerald-50 text-emerald-800 border-emerald-100",
  medium: "bg-amber-50 text-amber-800 border-amber-100",
  high: "bg-red-50 text-red-800 border-red-100",
};

function scoreColour(score) {
  if (score == null) return "text-slate-400";
  if (score >= 75) return "text-emerald-700";
  if (score >= 50) return "text-amber-700";
  return "text-red-700";
}

function ScoreBadge({ score, testid }) {
  const colour = scoreColour(score);
  return (
    <div
      className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3 py-1"
      data-testid={testid}
    >
      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
        Future-proof
      </span>
      <span className={`text-base font-semibold ${colour}`}>
        {score != null ? `${score}/100` : "—"}
      </span>
    </div>
  );
}

function RiskBadge({ raw, testid }) {
  const level = aiRiskLevel(raw);
  if (!level) return null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${RISK_BADGE[level]}`}
      data-testid={testid}
    >
      <ShieldCheck className="h-3 w-3" />
      {`AI risk: ${labelize(level)}`}
    </span>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</p>
      <p className="mt-1 text-sm font-medium text-slate-900">{value || "—"}</p>
    </div>
  );
}

function PathwayCard({ pkey, pathway, idx }) {
  const meta = PATHWAY_META[pkey] || {};
  const iconMap = { immediate_income: Zap, transitional: Compass, long_term_future_proof: Rocket };
  const Icon = iconMap[pkey] || Compass;

  return (
    <article
      className={`relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br ${meta.accent} p-7 lg:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]`}
      data-testid={`pathway-card-${pkey.replace(/_/g, "-")}`}
    >
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${meta.iconBg}`}>
            <Icon className="h-5 w-5" />
          </span>
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">
              {`Option ${idx + 1}  ·  ${meta.horizon}`}
            </p>
            <h3 className="text-xl lg:text-2xl font-medium text-slate-900 mt-0.5">
              {meta.label}
            </h3>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <ScoreBadge score={pathway.future_proof_score} testid={`pathway-${pkey}-score`} />
          <RiskBadge raw={pathway.ai_risk} testid={`pathway-${pkey}-risk`} />
        </div>
      </header>

      <p className="mt-5 text-sm text-slate-500 italic">{meta.blurb}</p>

      <div className="mt-5">
        <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Role</p>
        <p
          className="mt-1 text-lg lg:text-xl font-medium text-slate-900"
          data-testid={`pathway-${pkey}-role`}
        >
          {pathway.role_title || "—"}
        </p>
      </div>

      {pathway.reasoning ? (
        <p
          className="mt-4 text-sm lg:text-base text-slate-700 leading-relaxed"
          data-testid={`pathway-${pkey}-reasoning`}
        >
          {pathway.reasoning}
        </p>
      ) : null}

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label="Salary range" value={pathway.salary_range} testid={`pathway-${pkey}-salary`} />
        <Stat label="Time to income" value={pathway.time_to_income} testid={`pathway-${pkey}-time-to-income`} />
        <Stat label="Training timeline" value={pathway.training_timeline} testid={`pathway-${pkey}-training-timeline`} />
      </div>

      {pathway.training_required ? (
        <div className="mt-4 rounded-xl bg-white/70 border border-slate-100 p-4" data-testid={`pathway-${pkey}-training`}>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">
            Training required
          </p>
          <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">
            {pathway.training_required}
          </p>
        </div>
      ) : null}

      {Array.isArray(pathway.next_steps) && pathway.next_steps.length > 0 ? (
        <div className="mt-5">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
            Next steps
          </p>
          <ul className="space-y-2" data-testid={`pathway-${pkey}-next-steps`}>
            {pathway.next_steps.map((step, i) => (
              <li
                key={`${pkey}-${i}`}
                className="flex items-start gap-2 text-sm text-slate-700"
              >
                <CheckCircle2 className="h-4 w-4 mt-0.5 text-slate-400 flex-none" />
                <span>{step}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </article>
  );
}

function GenerateReportEmpty({ onGenerate, generating, error, status }) {
  const blocked = status === "draft";
  return (
    <section
      className="mt-10 rounded-3xl border border-slate-200 bg-white p-10 lg:p-14 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      data-testid="report-empty"
    >
      <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-slate-900 text-white">
        <Sparkles className="h-6 w-6" />
      </span>
      <h2 className="mt-6 text-2xl lg:text-3xl font-medium tracking-tight text-slate-900">
        {blocked ? "Finish your assessment first." : "No report yet."}
      </h2>
      <p className="mt-3 text-base text-slate-600 max-w-xl mx-auto">
        {blocked
          ? "Once you submit your assessment, we can ask three AIs to cross-check a personalised plan for you."
          : "Click generate to ask three AIs (OpenAI, Anthropic, Gemini) to cross-check a calm, prioritised plan based on your answers."}
      </p>

      {error ? (
        <div
          className="mt-6 mx-auto max-w-md rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700"
          data-testid="report-empty-error"
        >
          {error}
        </div>
      ) : null}

      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        {blocked ? (
          <Link
            to="/assessment"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-medium text-white shadow-md hover:bg-slate-800 hover:-translate-y-0.5 transition-all"
            data-testid="report-go-to-assessment-button"
          >
            Go to assessment
            <ArrowRight className="h-4 w-4" />
          </Link>
        ) : (
          <button
            type="button"
            onClick={onGenerate}
            disabled={generating}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-medium text-white shadow-md hover:bg-slate-800 hover:-translate-y-0.5 transition-all disabled:opacity-60"
            data-testid="report-generate-button"
          >
            {generating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Compass className="h-4 w-4" />
            )}
            {generating ? "Generating (this can take 30–60s)…" : "Generate Report"}
          </button>
        )}
        <Link
          to="/dashboard"
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-all"
          data-testid="report-back-dashboard-button"
        >
          Back to dashboard
        </Link>
      </div>
    </section>
  );
}

function buildActionPlan(ai) {
  const get = (k) => ai?.pathways?.[k]?.next_steps || [];
  return {
    day7: get("immediate_income").slice(0, 3),
    day30: get("transitional").slice(0, 3),
    day90: get("long_term_future_proof").slice(0, 3),
  };
}

function ActionColumn({ title, badge, items, testid }) {
  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-7"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-slate-900">{title}</h3>
        <span className="inline-flex items-center rounded-full bg-slate-900 text-white text-xs font-medium px-3 py-1">
          {badge}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500 italic">No specific actions yet.</p>
      ) : (
        <ol className="mt-4 space-y-3">
          {items.map((it, i) => (
            <li
              key={`${i}-${typeof it === "string" ? it.slice(0, 24) : i}`}
              className="flex items-start gap-3 text-sm text-slate-700"
              data-testid={`${testid}-item-${i}`}
            >
              <span className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-slate-100 text-xs font-medium text-slate-700">
                {i + 1}
              </span>
              <span>{it}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function SalaryGuidance({ assessment, pathways }) {
  const salary = assessment?.salary || {};
  const target = salary.target_annual;
  const minimum = salary.minimum_annual;
  const currency = salary.currency || "USD";

  const ranges = PATHWAY_ORDER.map((k) => ({
    key: k,
    label: PATHWAY_META[k].label,
    value: pathways?.[k]?.salary_range,
  }));

  return (
    <section
      className="mt-10 rounded-3xl border border-slate-200 bg-white p-7 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      data-testid="report-salary-guidance"
    >
      <header className="flex items-center gap-3 mb-6">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
          <Banknote className="h-5 w-5" />
        </span>
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">
            Salary guidance
          </p>
          <h2 className="text-2xl font-medium text-slate-900">What each path can pay</h2>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h3 className="text-sm font-medium text-slate-700 mb-3">Your stated needs</h3>
          <dl className="grid grid-cols-2 gap-3">
            <Stat
              label="Minimum"
              value={fmtMoney(minimum, currency) || "Not provided"}
              testid="report-your-min"
            />
            <Stat
              label="Target"
              value={fmtMoney(target, currency) || "Not provided"}
              testid="report-your-target"
            />
          </dl>
          {salary.open_to_lower_during_transition === true ? (
            <p className="mt-3 text-xs text-slate-500">
              {"You're open to lower pay during the transition."}
            </p>
          ) : null}
        </div>
        <div>
          <h3 className="text-sm font-medium text-slate-700 mb-3">Per pathway (from the AIs)</h3>
          <ul className="space-y-2">
            {ranges.map((r) => (
              <li
                key={r.key}
                className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 border border-slate-100 px-4 py-3"
                data-testid={`report-salary-${r.key.replace(/_/g, "-")}`}
              >
                <span className="text-sm font-medium text-slate-700">{r.label}</span>
                <span className="text-sm text-slate-900">{r.value || "—"}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function RetrainingRecommendations({ pathways, assessment }) {
  const retrain = assessment?.retraining_capacity || {};
  const rows = PATHWAY_ORDER.map((k) => ({
    key: k,
    label: PATHWAY_META[k].label,
    required: pathways?.[k]?.training_required,
    timeline: pathways?.[k]?.training_timeline,
  })).filter((r) => r.required || r.timeline);

  return (
    <section
      className="mt-10 rounded-3xl border border-slate-200 bg-white p-7 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      data-testid="report-retraining"
    >
      <header className="flex items-center gap-3 mb-6">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
          <GraduationCap className="h-5 w-5" />
        </span>
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">
            Retraining recommendations
          </p>
          <h2 className="text-2xl font-medium text-slate-900">
            What to learn and when
          </h2>
        </div>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <Stat
          label="Hours / week you said you have"
          value={retrain.hours_per_week_available ?? "—"}
          testid="report-retraining-hours"
        />
        <Stat
          label="Budget for courses"
          value={
            retrain.budget_for_courses != null
              ? fmtMoney(retrain.budget_for_courses, assessment?.salary?.currency)
              : "—"
          }
          testid="report-retraining-budget"
        />
        <Stat
          label="Preferred formats"
          value={
            Array.isArray(retrain.preferred_formats) && retrain.preferred_formats.length
              ? retrain.preferred_formats.map(labelize).join(", ")
              : "—"
          }
          testid="report-retraining-formats"
        />
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-slate-500 italic">No specific retraining requirements yet.</p>
      ) : (
        <ol className="space-y-4">
          {rows.map((r, i) => (
            <li
              key={r.key}
              className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5 lg:p-6"
              data-testid={`report-retraining-row-${r.key.replace(/_/g, "-")}`}
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h3 className="text-base font-medium text-slate-900">
                  {`Step ${i + 1} · ${r.label}`}
                </h3>
                {r.timeline ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 px-3 py-1 text-xs font-medium text-slate-700">
                    <CalendarDays className="h-3 w-3" />
                    {r.timeline}
                  </span>
                ) : null}
              </div>
              {r.required ? (
                <p className="mt-2 text-sm text-slate-700 leading-relaxed">{r.required}</p>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

export default function CareerReport() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState("");
  const [rescreening, setRescreening] = useState(false);
  const [rescreenError, setRescreenError] = useState("");
  const [sub, setSub] = useState(null);

  useEffect(() => {
    let active = true;
    api.get("/billing/me").then(({ data }) => { if (active) setSub(data); }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data: doc } = await api.get("/assessment/me");
        if (active) setData(doc);
      } catch (err) {
        if (active)
          setLoadError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const onGenerate = async () => {
    if (generating) return;
    setGenerating(true);
    setGenerateError("");
    try {
      const { data: doc } = await api.post("/assessment/me/generate-report");
      setData(doc);
    } catch (err) {
      setGenerateError(
        formatApiErrorDetail(err.response?.data?.detail) || err.message
      );
    } finally {
      setGenerating(false);
    }
  };

  const onRescreen = async () => {
    if (rescreening) return;
    setRescreening(true);
    setRescreenError("");
    try {
      const { data: doc } = await api.post("/assessment/me/screen-support");
      setData(doc);
    } catch (err) {
      setRescreenError(
        formatApiErrorDetail(err.response?.data?.detail) || err.message
      );
    } finally {
      setRescreening(false);
    }
  };

  if (!data && !loadError) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="career-report-page">
        <Navbar />
        <main className="pt-24 lg:pt-28 pb-20">
          <div className="max-w-5xl mx-auto px-6 lg:px-12 flex items-center gap-3 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading your report…
          </div>
        </main>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="career-report-page">
        <Navbar />
        <main className="pt-24 lg:pt-28 pb-20">
          <div className="max-w-2xl mx-auto px-6 lg:px-12 text-center">
            <p className="text-red-700" data-testid="career-report-load-error">
              {loadError}
            </p>
          </div>
        </main>
      </div>
    );
  }

  const ai = data.ai_recommendation;
  const reportExists = hasReport(data);

  // ---- Empty state ----
  if (!reportExists) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="career-report-page">
        <Navbar />
        <main className="pt-24 lg:pt-28 pb-20">
          <div className="max-w-3xl mx-auto px-6 lg:px-12">
            <Link
              to="/dashboard"
              className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1.5"
              data-testid="career-report-back-link"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to dashboard
            </Link>
            <div className="mt-4">
              <p className="text-sm uppercase tracking-[0.18em] text-sky-700 font-medium">
                Career report
              </p>
              <h1 className="mt-2 text-3xl lg:text-4xl font-medium tracking-tight text-slate-900">
                {`Your personalised plan, ${user?.name?.split(/[ @]/)[0] || "there"}.`}
              </h1>
            </div>
            <GenerateReportEmpty
              onGenerate={onGenerate}
              generating={generating}
              error={generateError}
              status={data.status}
            />
          </div>
        </main>
      </div>
    );
  }

  // ---- Full report ----
  const plan = buildActionPlan(ai);
  const summary = deriveCandidateSummary(data);
  const generatedAt = ai.generated_at ? new Date(ai.generated_at) : null;
  const allMocked = ai.all_mocked === true;
  const providers = Array.isArray(ai.providers) ? ai.providers : [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="career-report-page">
      <Navbar />
      <main className="pt-24 lg:pt-28 pb-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <Link
            to="/dashboard"
            className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1.5"
            data-testid="career-report-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to dashboard
          </Link>

          <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
            <div className="max-w-3xl">
              <p className="text-sm uppercase tracking-[0.18em] text-sky-700 font-medium">
                Career report
              </p>
              <h1
                className="mt-1.5 text-3xl lg:text-4xl font-medium tracking-tight text-slate-900"
                data-testid="career-report-heading"
              >
                {`Your personalised plan, ${user?.name?.split(/[ @]/)[0] || "there"}.`}
              </h1>
              <p className="mt-3 text-base text-slate-600">
                {`Three calm pathways — pick what fits your timing, your money, and your nerves.`}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800"
                data-testid="career-report-status-pill"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Report ready
              </span>
              <button
                type="button"
                onClick={onGenerate}
                disabled={generating}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                data-testid="career-report-regenerate-button"
              >
                {generating ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5" />
                )}
                {generating ? "Regenerating…" : "Regenerate"}
              </button>
            </div>
          </div>

          {generateError ? (
            <div
              className="mt-4 rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700"
              data-testid="career-report-regen-error"
            >
              {generateError}
            </div>
          ) : null}

          {/* Candidate summary */}
          <section
            className="mt-10 rounded-3xl border border-slate-200 bg-white p-7 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
            data-testid="career-report-candidate-summary"
          >
            <header className="flex items-center gap-3 mb-4">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-white">
                <User className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">
                  Candidate summary
                </p>
                <h2 className="text-2xl font-medium text-slate-900">
                  How we read your situation
                </h2>
              </div>
            </header>
            <p
              className="text-base text-slate-700 leading-relaxed"
              data-testid="career-report-summary-text"
            >
              {summary}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              {generatedAt ? (
                <span data-testid="career-report-generated-at">
                  {`Generated ${generatedAt.toLocaleString()}`}
                </span>
              ) : null}
              {providers.length ? (
                <>
                  <span aria-hidden="true">·</span>
                  <span data-testid="career-report-providers">
                    {`Consulted: ${providers.join(", ")}`}
                  </span>
                </>
              ) : null}
              {allMocked ? (
                <span
                  className="inline-flex items-center rounded-full bg-amber-50 border border-amber-100 px-2.5 py-0.5 text-amber-800"
                  data-testid="career-report-placeholder-flag"
                >
                  Placeholder mode
                </span>
              ) : null}
            </div>
          </section>

          {/* Paywall teaser when not subscribed */}
          {sub && sub.status !== "active" ? (
            <PaywallTeaser assessment={data} />
          ) : null}

          {/* Pathways header */}
          <div className="mt-12 flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
              <TrendingUp className="h-5 w-5" />
            </span>
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">
                Three pathways
              </p>
              <h2 className="text-2xl font-medium text-slate-900">
                Pick one (or sequence them).
              </h2>
            </div>
          </div>

          <div
            className="mt-6 grid grid-cols-1 xl:grid-cols-3 gap-6"
            data-testid="career-report-pathways"
          >
            {PATHWAY_ORDER.map((k, i) => (
              <PathwayCard key={k} pkey={k} pathway={ai.pathways?.[k] || {}} idx={i} />
            ))}
          </div>

          {/* Retraining */}
          <RetrainingRecommendations pathways={ai.pathways} assessment={data} />

          {/* Salary guidance */}
          <SalaryGuidance assessment={data} pathways={ai.pathways} />

          {/* Government support (e.g. Australia) */}
          <GovernmentSupportSection
            review={data.government_support_review}
            countryConfigured={Boolean(
              data?.government_support?.country ||
                data?.location_travel_relocation?.current_country
            )}
            onRescreen={onRescreen}
            rescreening={rescreening}
            rescreenError={rescreenError}
          />

          {/* 7 / 30 / 90 plan */}
          <section className="mt-10" data-testid="career-report-action-plan">
            <header className="flex items-center gap-3 mb-5">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700">
                <Target className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">
                  7 / 30 / 90 day plan
                </p>
                <h2 className="text-2xl font-medium text-slate-900">
                  The smallest next thing — three times.
                </h2>
              </div>
            </header>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <ActionColumn
                title="Next 7 days"
                badge="Now"
                items={plan.day7}
                testid="action-plan-7"
              />
              <ActionColumn
                title="Next 30 days"
                badge="Soon"
                items={plan.day30}
                testid="action-plan-30"
              />
              <ActionColumn
                title="Next 90 days"
                badge="Build"
                items={plan.day90}
                testid="action-plan-90"
              />
            </div>
          </section>

          {/* Footer CTA */}
          <section className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-3xl border border-slate-200 bg-white px-7 py-6">
            <div className="flex items-center gap-3">
              <BookOpenCheck className="h-5 w-5 text-slate-500" />
              <p className="text-sm text-slate-600">
                Your assessment data drives this report. If something looks off, edit your answers.
              </p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/assessment"
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                data-testid="career-report-edit-assessment-link"
              >
                Edit assessment
              </Link>
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white shadow-md hover:bg-slate-800"
                data-testid="career-report-go-dashboard-button"
              >
                Back to dashboard
              </button>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
